import { useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Fila de folhas de abertura — garante UMA folha por vez, nunca uma em cima da
// outra. Ordem (número menor passa na frente):
//   1 = instalar o app  ·  2 = avisos no celular  ·  3 = tribo  ·  4 = WhatsApp
// A seguinte abre 1 s depois que a anterior foi respondida ou fechada.
//
// No máximo DUAS folhas por abertura do app: o resto espera a próxima.
//
// Cada pedaço da tela que quer abrir uma folha chama `useVezDeFolha`. O hook
// devolve `true` só para o dono da vez; os outros ficam esperando a sua.
// ---------------------------------------------------------------------------

const PAUSA_MS = 1000;
const LIMITE_POR_ABERTURA = 2;

let atual: string | null = null;
let timer: number | null = null;
let exibidasNestaAbertura = 0;
const desejos = new Map<string, number>();
const ouvintes = new Set<() => void>();

function avisar(): void {
  ouvintes.forEach((fn) => fn());
}

function melhor(): string | null {
  let escolhido: string | null = null;
  let menor = Number.POSITIVE_INFINITY;
  desejos.forEach((prioridade, chave) => {
    if (prioridade < menor) {
      menor = prioridade;
      escolhido = chave;
    }
  });
  return escolhido;
}

function liberarAgora(): void {
  if (atual) return;
  if (exibidasNestaAbertura >= LIMITE_POR_ABERTURA) return; // o resto fica para a próxima abertura
  const proximo = melhor();
  if (!proximo) return;
  atual = proximo;
  exibidasNestaAbertura += 1;
  avisar();
}

function agendarProximo(): void {
  if (timer) window.clearTimeout(timer);
  timer = window.setTimeout(() => {
    timer = null;
    liberarAgora();
  }, PAUSA_MS);
}

/**
 * Pede a vez de mostrar a folha. `quer` diz se este ponto quer aparecer agora;
 * o retorno diz se é a vez dele. Prioridades menores aparecem antes.
 */
export function useVezDeFolha(identificador: string, prioridade: number, quer: boolean): boolean {
  const [, forcar] = useState(0);

  useEffect(() => {
    const ouvinte = () => forcar((n) => n + 1);
    ouvintes.add(ouvinte);
    return () => {
      ouvintes.delete(ouvinte);
    };
  }, []);

  useEffect(() => {
    if (quer) {
      desejos.set(identificador, prioridade);
      if (!atual) {
        // Sem ninguém aberto: só abre na hora se não houver pausa em andamento.
        if (!timer) liberarAgora();
      } else if (atual !== identificador) {
        const atualPrioridade = desejos.get(atual) ?? Number.POSITIVE_INFINITY;
        if (prioridade < atualPrioridade) {
          // Prioridade maior (número menor) passa na frente.
          atual = identificador;
        }
        avisar();
      }
    } else {
      desejos.delete(identificador);
      if (atual === identificador) {
        atual = null;
        avisar();
        agendarProximo();
      }
    }

    return () => {
      desejos.delete(identificador);
      if (atual === identificador) {
        atual = null;
        avisar();
        agendarProximo();
      }
    };
  }, [identificador, prioridade, quer]);

  return atual === identificador;
}